import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { collection, query, where, getDocs, deleteDoc, doc, orderBy } from 'firebase/firestore'
import { db } from '../../firebaseConfig'
import Swal from 'sweetalert2'

import UserNav from '../../components/navbar/user-nav'
import AdminSidebar from '../../components/admin-sidebar'
import FooterTop from '../../components/footer-top'
import Footer from '../../components/footer'
import PageTitle from '../../components/page-title'
import { LoadingMessage, EmptyState, SkeletonTable } from '../../components/loading-states'
import { useUser } from '../../context/UserContext'
import { useProperties, Propiedad } from '../../hooks/useProperties'

interface Favorito {
  id: string
  userId: string
  propertyId: string
  fechaAgregado: any
  notasUsuario?: string
}

interface FavoritoConPropiedad extends Favorito {
  propiedad: Propiedad | null
}

export default function BookmarkList() {
  const { t } = useTranslation()
  const { user } = useUser()
  const { fetchPropiedadById, formatearPrecio, formatearDireccion } = useProperties()

  const [show, setShow] = useState<boolean>(false)
  const [favoritos, setFavoritos] = useState<FavoritoConPropiedad[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  // Cargar favoritos del usuario
  const cargarFavoritos = useCallback(async () => {
    if (!user?.id) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Buscar favoritos del usuario
      const favoritosQuery = query(
        collection(db, 'favoritos'),
        where('userId', '==', user.id),
        orderBy('fechaAgregado', 'desc')
      )

      const favoritosSnap = await getDocs(favoritosQuery)

      if (favoritosSnap.empty) {
        setFavoritos([])
        setLoading(false)
        return
      }

      // Cargar propiedades de cada favorito
      const favoritosConPropiedades: FavoritoConPropiedad[] = await Promise.all(
        favoritosSnap.docs.map(async (docSnap) => {
          const favoritoData = { id: docSnap.id, ...docSnap.data() } as Favorito
          const propiedad = await fetchPropiedadById(favoritoData.propertyId)
          return {
            ...favoritoData,
            propiedad
          }
        })
      )

      setFavoritos(favoritosConPropiedades)
    } catch (err: any) {
      console.error('Error cargando favoritos:', err)
      setError('Error al cargar favoritos. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }, [user?.id, fetchPropiedadById])

  // Cargar al montar
  useEffect(() => {
    cargarFavoritos()
  }, [cargarFavoritos])

  // Eliminar favorito
  const handleEliminarFavorito = async (favoritoId: string, nombrePropiedad: string) => {
    const result = await Swal.fire({
      title: '¿Eliminar de favoritos?',
      text: `¿Deseas eliminar "${nombrePropiedad}" de tus favoritos?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    })

    if (!result.isConfirmed) return

    setDeleting(favoritoId)

    try {
      await deleteDoc(doc(db, 'favoritos', favoritoId))

      // Actualizar lista local
      setFavoritos(prev => prev.filter(f => f.id !== favoritoId))

      Swal.fire({
        icon: 'success',
        title: 'Eliminado',
        text: 'La propiedad ha sido eliminada de tus favoritos',
        timer: 2000,
        showConfirmButton: false
      })
    } catch (err) {
      console.error('Error eliminando favorito:', err)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo eliminar el favorito. Intenta de nuevo.'
      })
    } finally {
      setDeleting(null)
    }
  }

  // Formatear fecha
  const formatearFecha = (fecha: any): string => {
    if (!fecha) return ''
    const date = fecha.toDate ? fecha.toDate() : new Date(fecha)
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  return (
    <>
      <UserNav />

      <PageTitle
        title="Mis Favoritos"
        subtitle="Propiedades guardadas para ver después"
      />

      <section className="bg-light py-5">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="filter_search_opt">
                <button
                  onClick={() => setShow(!show)}
                  className="btn btn-dark full-width mb-4"
                  aria-label="Mostrar navegación del dashboard"
                  aria-expanded={show}
                >
                  Navegación del Dashboard
                  <i className="fa-solid fa-bars ms-2" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-md-12">
              <div className="simple-sidebar sm-sidebar" id="filter_search">
                <div className="search-sidebar_header">
                  <h4 className="ssh_heading">Cerrar</h4>
                  <button
                    className="w3-bar-item w3-button w3-large"
                    onClick={() => setShow(false)}
                    aria-label="Cerrar panel de navegación"
                    type="button"
                  >
                    <i className="fa-regular fa-circle-xmark fs-5 text-muted-2" aria-hidden="true"></i>
                  </button>
                </div>

                <div className="sidebar-widgets">
                  <AdminSidebar show={show} setShow={setShow} />
                </div>
              </div>
            </div>

            <div className="col-lg-9 col-md-12">
              <div className="dashboard-wraper">
                <div className="form-submit d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                  <div>
                    <h4 className="mb-1">
                      <i className="fa-solid fa-heart text-danger me-2"></i>
                      Propiedades Favoritas
                    </h4>
                    <p className="text-muted mb-0">
                      {favoritos.length} {favoritos.length === 1 ? 'propiedad guardada' : 'propiedades guardadas'}
                    </p>
                  </div>
                  <Link to="/modulob/grid" className="btn btn-primary">
                    <i className="fa-solid fa-search me-2"></i>
                    Buscar más propiedades
                  </Link>
                </div>

                {/* Estados de carga/error/vacío */}
                {loading ? (
                  <SkeletonTable rows={5} columns={4} showHeader={true} />
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    <i className="fa-solid fa-exclamation-triangle me-2"></i>
                    {error}
                    <button className="btn btn-link" onClick={cargarFavoritos}>
                      Reintentar
                    </button>
                  </div>
                ) : !user ? (
                  <EmptyState
                    icon="fa-user-lock"
                    title="Inicia sesión"
                    message="Debes iniciar sesión para ver tus propiedades favoritas"
                    actionText="Ir al inicio"
                    onAction={() => window.location.href = '/'}
                  />
                ) : favoritos.length === 0 ? (
                  <EmptyState
                    icon="fa-heart"
                    title="No tienes favoritos"
                    message="Cuando encuentres propiedades que te interesen, agrégalas a favoritos para verlas después"
                    actionText="Buscar propiedades"
                    onAction={() => window.location.href = '/modulob/grid'}
                  />
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">
                            <i className="fa-solid fa-image me-2"></i>
                            Propiedad
                          </th>
                          <th scope="col">
                            <i className="fa-solid fa-location-dot me-2"></i>
                            Ubicación
                          </th>
                          <th scope="col">
                            <i className="fa-solid fa-dollar-sign me-2"></i>
                            Precio
                          </th>
                          <th scope="col">
                            <i className="fa-solid fa-calendar me-2"></i>
                            Guardado
                          </th>
                          <th scope="col" className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {favoritos.map((fav) => (
                          <tr key={fav.id}>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <img
                                  src={fav.propiedad?.imagen || (fav.propiedad?.fotos && fav.propiedad.fotos[0]) || '/assets/img/placeholder-property.jpg'}
                                  alt={fav.propiedad?.nombre || 'Propiedad'}
                                  className="rounded"
                                  style={{ width: '80px', height: '60px', objectFit: 'cover' }}
                                />
                                <div>
                                  <h6 className="mb-1">
                                    <Link
                                      to={`/modulob/single-property-1/${fav.propertyId}`}
                                      className="text-dark text-decoration-none"
                                    >
                                      {fav.propiedad?.nombre || fav.propiedad?.titulo || 'Propiedad no disponible'}
                                    </Link>
                                  </h6>
                                  <span className={`badge ${fav.propiedad?.tipo_operacion === 'renta' ? 'bg-info' : 'bg-success'}`}>
                                    {fav.propiedad?.tipo_operacion === 'renta' ? 'En Renta' : 'En Venta'}
                                  </span>
                                  {fav.propiedad?.tipo_propiedad && (
                                    <span className="badge bg-secondary ms-1">
                                      {fav.propiedad.tipo_propiedad}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="text-muted">
                                {fav.propiedad?.ciudad || formatearDireccion(fav.propiedad?.direccion) || 'Sin ubicación'}
                              </span>
                            </td>
                            <td>
                              <strong className="text-primary">
                                {fav.propiedad ? formatearPrecio(fav.propiedad.precio_actual_usd || fav.propiedad.precio || 0) : '-'}
                              </strong>
                            </td>
                            <td>
                              <small className="text-muted">
                                {formatearFecha(fav.fechaAgregado)}
                              </small>
                            </td>
                            <td className="text-center">
                              <div className="btn-group" role="group" aria-label="Acciones de favorito">
                                <Link
                                  to={`/modulob/single-property-1/${fav.propertyId}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Ver propiedad"
                                  aria-label={`Ver detalles de ${fav.propiedad?.nombre || 'propiedad'}`}
                                >
                                  <i className="fa-solid fa-eye"></i>
                                </Link>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleEliminarFavorito(fav.id, fav.propiedad?.nombre || 'esta propiedad')}
                                  disabled={deleting === fav.id}
                                  title="Eliminar de favoritos"
                                  aria-label={`Eliminar ${fav.propiedad?.nombre || 'propiedad'} de favoritos`}
                                >
                                  {deleting === fav.id ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                  ) : (
                                    <i className="fa-solid fa-trash-can"></i>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Información adicional */}
                {favoritos.length > 0 && (
                  <div className="alert alert-info mt-4" role="alert">
                    <i className="fa-solid fa-lightbulb me-2"></i>
                    <strong>Tip:</strong> Puedes agregar propiedades a favoritos desde la página de detalle de cada propiedad haciendo clic en el icono de corazón.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  )
}
